import AuthedImage from '../components/AuthedImage';
import { openWithAuth } from '../utils/authedResource';
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button, Table, Tag, Space, DatePicker, Input, Select, message, Upload, Modal as AntModal, Card, Row, Col, Spin, Tooltip, Tabs, Segmented, Switch, Empty, Form, Radio, Progress, Checkbox, Dropdown } from 'antd';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import WideModal from '../components/WideModal';
import http from '../utils/http'; // axios instance
import { getToken } from '../utils/auth';
import { Line, XAxis, YAxis, Tooltip as ReTooltip, CartesianGrid, ResponsiveContainer, Bar, Legend, ComposedChart } from 'recharts';

const CATEGORIES = ['高血压','高血糖','饮酒人员','违章人员'];
const ALL_KEY = 'ALL';
const categoryColors = { '高血压':'volcano','高血糖':'purple','饮酒人员':'geekblue','违章人员':'red' };

const SpecialMonitoringPage = () => {
  const [activeCat, setActiveCat] = useState('高血压'); // 或 ALL
  // 记录列表选中项用于 HR 趋势展示，需在使用之前定义
  const [selectedRecordForTrend, setSelectedRecordForTrend] = useState(null);
  const [data, setData] = useState([]); const [total,setTotal]=useState(0); const [page,setPage]=useState(1); const [pageSize,setPageSize]=useState(20);
  const [loading,setLoading]=useState(false);
  const [stats, setStats] = useState({ counts:[], trend:[], days:30 });
  const [daysRange, setDaysRange] = useState(30); // 7/14/30
  const [smooth, setSmooth] = useState(true); // 平滑曲线开关
  const [filters, setFilters] = useState({ dateRange:null, q:'', responsible:null, employee:null });
  // 用于防止并发请求覆盖：每次 fetchList 生成一个递增 id，只处理最新一次
  const listReqIdRef = useRef(0);
  const [modalOpen,setModalOpen]=useState(false); const [editing,setEditing]=useState(null);
  const [saving,setSaving]=useState(false);
  const [formVals,setFormVals]=useState({ monitor_date: dayjs().format('YYYY-MM-DD') });
  const [selectedTaskId, setSelectedTaskId] = useState(null); // 新增记录时选中的监测任务
  const [photoFile,setPhotoFile]=useState(null); const [photoPreview,setPhotoPreview]=useState(null); const [removePhoto,setRemovePhoto]=useState(false);
  const [empOptions,setEmpOptions]=useState([]); const [respOptions,setRespOptions]=useState([]); const [empLoading,setEmpLoading]=useState(false);
  // 任务管理状态
  const [taskModalOpen,setTaskModalOpen]=useState(false);
  const [editingTask,setEditingTask]=useState(null);
  const [taskSaving,setTaskSaving]=useState(false);
  const [tasks,setTasks]=useState([]);
  const [allTasksRaw, setAllTasksRaw] = useState([]); // 未过滤的原始任务集合
  const [showActiveOnly,setShowActiveOnly]=useState(false);
  const [copySource,setCopySource]=useState(null);
  const [completionData,setCompletionData]=useState([]);
  const [responsibleSummary,setResponsibleSummary] = useState([]); // 责任人汇总
  const [taskForm] = Form.useForm();
  // AntD Form 内部字段变化不会驱动父组件重渲染；使用 useWatch 保证“保存置灰/条件字段”实时响应
  const taskEmployeeId = Form.useWatch('employee_id', taskForm);
  const taskResponsibleId = Form.useWatch('responsible_id', taskForm);
  const taskCategories = Form.useWatch('categories', taskForm);
  const taskFrequencyType = Form.useWatch('frequency_type', taskForm);
  // HR 用户选择记录后显示该监测对象+类别趋势
  const [recordTrend, setRecordTrend] = useState([]);
  const [recordTrendLoading, setRecordTrendLoading] = useState(false);
  const debounceRef = useRef();
  // 缓存: key 结构: type|employee|cat|days|activeFlag
  const cacheRef = useRef(new Map());
  const cacheTTL = 1000 * 60; // 1 分钟
  const makeKey = React.useCallback((type, { employee, cat, days, active })=> `${type}|${employee||''}|${cat||''}|${days||''}|${active||''}` , []);
  const getCache = React.useCallback((key)=>{
    const hit = cacheRef.current.get(key);
    if(!hit) return null;
    if(Date.now() - hit.time > cacheTTL){ cacheRef.current.delete(key); return null; }
    return hit.data;
  }, []);
  const setCache = React.useCallback((key,data)=> cacheRef.current.set(key, { data, time: Date.now() }), []);
  const invalidateAllCaches = React.useCallback(()=> { cacheRef.current.clear(); }, []);
  const authHeader = React.useCallback(()=> ({ 'Authorization':'Bearer '+getToken() }), []);
  // 使用 AuthContext 权限
  const { hasPermission, user } = useAuth() || { hasPermission: () => false, user:null };
  // 角色名称映射（后端 roles.name -> 显示描述）
  const roleNameMap = {
    hr: 'HR',
    project_manager: '项目经理',
    deputy_manager: '副经理'
  };
  const parsedRoles = useMemo(()=>{
    if(!user) return [];
    return Array.isArray(user.roles)? user.roles : (typeof user.roles === 'string'? user.roles.split(','): []);
  }, [user]);
  // 角色优先级：项目经理 > 副经理 > HR > 其它(取第一个)
  const primaryRoleDesc = useMemo(()=>{
    if(!parsedRoles.length) return '—';
    const lower = parsedRoles.map(r=> String(r).trim().toLowerCase());
    if(lower.includes('project_manager')) return roleNameMap.project_manager;
    if(lower.includes('deputy_manager')) return roleNameMap.deputy_manager;
    if(lower.includes('hr')) return roleNameMap.hr;
    // 其它角色原样展示第一个
    const first = parsedRoles[0];
    return roleNameMap[first] || first;
  }, [parsedRoles]);
  const isProjectManagerLike = useMemo(()=>{
    const ls = parsedRoles.map(r=> String(r).toLowerCase());
    return ls.includes('project_manager') || ls.includes('deputy_manager');
  }, [parsedRoles]);
  // 更稳健的 HR 角色判定：后端返回的 user.roles 可能是数组或逗号分隔字符串
  const isHr = useMemo(()=> parsedRoles.some(r=> typeof r === 'string' && r.toLowerCase() === 'hr'), [parsedRoles]);
  // HR 用户的员工ID（负责的任务与记录存储在 tasks/responsible_id 以及 records.responsible_id）
  // 后端登录返回的 user.name 是 employee_name；需要映射到员工表 ID
  const [hrEmployeeId, setHrEmployeeId] = useState(null);
  useEffect(()=>{
    let mounted = true;
    async function resolveHrEmployee(){
      if(!isHr || !user?.name){ setHrEmployeeId(null); return; }
      try {
        // 根据姓名模糊查找，取精确匹配第一条（避免同名冲突问题可扩展为额外字段）
        const res = await http.get('/employees', { params:{ name: user.name }, headers: authHeader() });
        const list = Array.isArray(res.data)? res.data: [];
        const exact = list.find(e=> e.name === user.name);
        if(mounted) setHrEmployeeId(exact? exact.id: (list[0]? list[0].id: null));
        if(!exact && list.length>1){ console.warn('[SpecialMonitoring] HR 员工姓名存在多匹配，已使用第一条:', list[0]); }
      } catch(err){ if(mounted){ setHrEmployeeId(null); console.warn('[SpecialMonitoring] 解析 HR 员工ID失败:', err.message); } }
    }
    resolveHrEmployee();
    return ()=>{ mounted=false; };
  }, [isHr, user, authHeader]);
  const canManageTasks = hasPermission(['special_monitoring_task_manage']) || hasPermission(['manage_education_training']);
  const canManageRecords = hasPermission(['special_monitoring_record_manage']) || hasPermission(['manage_education_training']);
  const canViewAllMonitoring = hasPermission(['special_monitoring_view_all']);

  // 统计数据（类别 counts + 趋势）获取与缓存
  // fetchStats 依赖 getCache/setCache/makeKey 不会变化，添加依赖数组保持稳定
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchStats = useCallback(async ()=>{
    try {
      const baseParams = { days: daysRange };
      if(filters.employee) baseParams.employee_id = filters.employee;
      if(activeCat === ALL_KEY){
        const cacheKey = makeKey('statsAll', { employee: filters.employee, cat:'ALL', days: daysRange });
        const cached = getCache(cacheKey); if(cached){ setStats(cached); return; }
        const allCounts = []; const combinedTrendMap = new Map();
        const results = await Promise.all(CATEGORIES.map(async cat => {
          const params = { ...baseParams, category: cat };
          try { const res = await http.get('/special-monitorings/stats', { params, headers: authHeader() }); return { cat, data: res.data||{ counts:[], trend:[] } }; }
          catch(e){ return { cat, data:{ counts:[], trend:[] } }; }
        }));
        results.forEach(({ data })=>{
          if(Array.isArray(data.counts)) data.counts.forEach(c=> allCounts.push(c));
          if(Array.isArray(data.trend)) data.trend.forEach(t=>{ const d = dayjs(t.monitor_date).format('YYYY-MM-DD'); combinedTrendMap.set(d, (combinedTrendMap.get(d)||0) + (t.count||0)); });
        });
        const trend = Array.from(combinedTrendMap.entries()).map(([monitor_date,count])=>({ monitor_date, count }));
        const merged = { counts: allCounts, trend, days: daysRange };
        setStats(merged); setCache(cacheKey, merged);
      } else {
        const cacheKey = makeKey('stats', { employee: filters.employee, cat: activeCat, days: daysRange });
        const cached = getCache(cacheKey); if(cached){ setStats(cached); return; }
        const params = { ...baseParams, category: activeCat };
        const res = await http.get('/special-monitorings/stats', { params, headers: authHeader() });
        const val = res.data||{ counts:[], trend:[] };
        setStats(val); setCache(cacheKey, val);
      }
    } catch(e){ /* ignore */ }
  }, [activeCat, filters.employee, daysRange]);

  // 员工多类别趋势数据加载
  // 已移除员工多类别趋势图，相关状态与加载逻辑删除以减少额外请求

  // 构造员工多类别折线数据，补全日期
  // employeeTrendLines 已不再使用，移除计算逻辑以减少无效依赖与内存占用

  // 补齐最近30天缺失的日期 - 当前类别趋势 (后端 trend 已按 category & employee 过滤)
  // filledTrend 之前用于补齐趋势折线图，当前界面已移除相关图表，故删除

  const fetchList = useCallback(async ()=>{
    const currentId = ++listReqIdRef.current;
    setLoading(true);
    try {
      const { q, responsible, employee, dateRange } = filters;
      const params = { category: activeCat, page, pageSize };
      if(q) params.q = q;
      if(responsible) params.responsible_id = responsible; // 页面筛选器仍可覆盖
      if(employee) params.employee_id = employee;
      if(dateRange){ params.start_date = dateRange[0].format('YYYY-MM-DD'); params.end_date = dateRange[1].format('YYYY-MM-DD'); }
      const res = await http.get('/special-monitorings', { params, headers: authHeader() });
      if(currentId === listReqIdRef.current){
        let records = res.data.records||[];
        // 非项目经理/副经理视角：仍按当前用户负责人过滤
  if(!canViewAllMonitoring && !isProjectManagerLike && user?.name){ records = records.filter(r=> r.responsible_name === user.name); }
        setData(records); setTotal(records.length); // total 改为过滤后长度
      }
    } catch(e){ if(currentId === listReqIdRef.current) message.error(e?.message||'加载列表失败'); }
    if(currentId === listReqIdRef.current) setLoading(false);
  }, [activeCat, page, pageSize, filters.q, filters.responsible, filters.employee, filters.dateRange, authHeader, user]);

  useEffect(()=>{ fetchList(); }, [fetchList]);
  useEffect(()=>{ fetchStats(); }, [fetchStats]);

  // 加载任务列表
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadTasks = useCallback(async ()=>{
    try {
      const params={};
      if(filters.employee) params.employee_id = filters.employee;
      if(activeCat && activeCat!==ALL_KEY) params.category = activeCat;
      // 不在请求阶段加入 active / mine 过滤，统一在派生层处理
      const cacheKey = makeKey('tasksRaw', { employee: filters.employee, cat: activeCat===ALL_KEY? 'ALL': activeCat });
      const cached = getCache(cacheKey);
      if(cached){ setAllTasksRaw(cached); return; }
      const res = await http.get('/special-monitorings/tasks', { params, headers: authHeader() });
      const arr = Array.isArray(res.data)? res.data: [];
      setAllTasksRaw(arr);
      setCache(cacheKey, arr);
    } catch(e){ /* ignore */ }
  }, [filters.employee, activeCat]);
  useEffect(()=>{ loadTasks(); }, [loadTasks]);
  // 权限变化后重新加载任务（防止晚写入权限后没有入口）
  useEffect(()=>{ if(canManageTasks){ loadTasks(); } }, [canManageTasks, loadTasks]);

  // 派生过滤后的任务列表
  useEffect(()=>{
    let derived = allTasksRaw.slice();
    if(showActiveOnly){ derived = derived.filter(t=> t.status==='ongoing'); }
    // 非项目经理/副经理过滤为本人的任务
  if(!canViewAllMonitoring && !isProjectManagerLike && user?.name){ derived = derived.filter(t=> t.responsible_name === user.name); }
    setTasks(derived);
  }, [allTasksRaw, showActiveOnly, user, isProjectManagerLike]);

  // 加载完成率数据
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadCompletion = useCallback( async ()=>{
    try {
      const params = { days: daysRange };
      if(filters.employee) params.employee_id = filters.employee;
      if(activeCat && activeCat!==ALL_KEY) params.category = activeCat;
      const cacheKey = makeKey('completion', { employee: filters.employee, cat: activeCat===ALL_KEY? 'ALL': activeCat, days: daysRange });
      const cached = getCache(cacheKey);
      if(cached){ setCompletionData(cached); return; }
      const res = await http.get('/special-monitorings/tasks/completion', { params, headers: authHeader() });
      const list = res.data?.data||[];
      setCompletionData(list); setCache(cacheKey, list);
    } catch(e){ setCompletionData([]); }
  }, [filters.employee, activeCat, daysRange]);
  useEffect(()=>{ loadCompletion(); }, [loadCompletion]);

  // 责任人汇总数据加载（进行中任务）
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadResponsibleSummary = useCallback(async ()=>{
    try {
      const cacheKey = 'responsibleSummary';
      const cached = getCache(cacheKey);
      if(cached){ setResponsibleSummary(cached); return; }
      const res = await http.get('/special-monitorings/tasks/responsible-summary', { headers: authHeader() });
      const arr = Array.isArray(res.data)? res.data: [];
      setResponsibleSummary(arr);
      setCache(cacheKey, arr);
    } catch(e){ setResponsibleSummary([]); }
  }, []);
  useEffect(()=>{ loadResponsibleSummary(); }, [loadResponsibleSummary]);

  // 解析监测值：返回 { systolic, diastolic, value, raw }
  const parseMonitorValue = (val, category)=>{
    if(!val) return { raw: val };
    const raw = String(val).trim();
    if(category === '高血压'){
      // 格式如 120/80 或 130-85 等，提取两个数值
      const parts = raw.split(/[\/-]/).map(p=> p.trim()).filter(Boolean);
      if(parts.length >= 2){
        const s = parseFloat(parts[0]);
        const d = parseFloat(parts[1]);
        return { systolic: isFinite(s)? s: undefined, diastolic: isFinite(d)? d: undefined, raw };
      }
      const single = parseFloat(raw);
      return { value: isFinite(single)? single: undefined, raw };
    }
    // 其它类别：尝试解析为单数值（可能含单位，如 '6.2mmol' -> 6.2）
    const match = raw.match(/([0-9]+(?:\.[0-9]+)?)/);
    if(match){
      const num = parseFloat(match[1]);
      if(isFinite(num)) return { value: num, raw };
    }
    return { raw };
  };

  // 加载选中记录趋势 (HR 专用)：按 monitor_value 展示
  const loadRecordTrend = useCallback(async (employeeId, category)=>{
    if(!employeeId || !category) { setRecordTrend([]); return; }
    setRecordTrendLoading(true);
    try {
      // 使用原始记录接口获取最近区间的记录（按日期范围）
      const start_date = dayjs().subtract(daysRange-1,'day').format('YYYY-MM-DD');
      const end_date = dayjs().format('YYYY-MM-DD');
      const params = { employee_id: employeeId, category, start_date, end_date, page:1, pageSize: 500 };
      const res = await http.get('/special-monitorings', { params, headers: authHeader() });
      const records = Array.isArray(res.data?.records)? res.data.records: [];
      // 以日期聚合：若多条，血压取最后一条的两个值；其它类别取平均值
      const byDate = new Map();
      for(const r of records){
        const d = dayjs(r.monitor_date).format('YYYY-MM-DD');
        const parsed = parseMonitorValue(r.monitor_value, category);
        if(!byDate.has(d)) byDate.set(d, []);
        byDate.get(d).push(parsed);
      }
      const filled = [];
      for(let i=daysRange-1;i>=0;i--){
        const date = dayjs().subtract(i,'day').format('YYYY-MM-DD');
        const arr = byDate.get(date) || [];
        let systolic, diastolic, value; let rawValues = arr.map(a=> a.raw).filter(Boolean);
        if(category==='高血压'){ // 取最后一条（更接近日常记录习惯）
          if(arr.length){
            const last = arr[arr.length-1];
            systolic = last.systolic; diastolic = last.diastolic; value = last.value; // value 可能存在单值形式
          }
        } else if(arr.length){
          // 平均单值
          const nums = arr.map(a=> a.value).filter(v=> v!==undefined);
          if(nums.length){ value = Number((nums.reduce((s,n)=> s+n,0)/nums.length).toFixed(2)); }
        }
        filled.push({ date, systolic, diastolic, value, rawValues });
      }
      setRecordTrend(filled);
    } catch(e){ setRecordTrend([]); }
    setRecordTrendLoading(false);
  }, [daysRange, authHeader]);

  useEffect(()=>{
    if(isHr && selectedRecordForTrend){
      loadRecordTrend(selectedRecordForTrend.employee_id, selectedRecordForTrend.category);
    }
  }, [isHr, selectedRecordForTrend, loadRecordTrend]);

  const openNewTask = ()=>{
    if(!canManageTasks){ message.warning('无任务管理权限'); return; }
    setEditingTask(null); setCopySource(null); taskForm.resetFields();
    const initialCats = activeCat===ALL_KEY? []: [activeCat];
    taskForm.setFieldsValue({ categories: initialCats, frequency_type:'daily', start_date: dayjs() });
    setTaskModalOpen(true);
  };
  const openEditTask = (task)=>{ setEditingTask(task); taskForm.setFieldsValue({ ...task, start_date: dayjs(task.start_date), end_date: task.end_date? dayjs(task.end_date): null, responsible_id: task.responsible_id }); setTaskModalOpen(true); };
  const openCopyTask = (task)=>{ setEditingTask(null); setCopySource(task); taskForm.resetFields(); taskForm.setFieldsValue({ categories:[task.category], frequency_type:task.frequency_type, frequency_value: task.frequency_type==='every_n_days'? task.frequency_value: undefined, start_date: dayjs(), remark: task.remark, employee_id: task.employee_id, responsible_id: task.responsible_id }); setTaskModalOpen(true); };
  const handleDeleteTask = (task)=>{
    AntModal.confirm({
      title:'删除任务确认',
      content:`确定删除该任务? ${task.category}`,
      onOk: async ()=>{
        try {
          await http.delete(`/special-monitorings/tasks/${task.id}`, { headers: authHeader() });
          message.success('已删除');
          // 先本地移除，提高界面响应
          setTasks(prev=> prev.filter(t=> t.id!==task.id));
          invalidateAllCaches();
          // 异步刷新最新统计
          loadTasks();
          loadCompletion();
        } catch(e){ message.error('删除失败'); }
      }
    });
  };
  const handleSaveTask = async ()=>{
    try {
      const vals = await taskForm.validateFields();
      if(vals.frequency_type==='every_n_days' && !vals.frequency_value){ message.warning('请输入N天频率值'); return; }
      const categories = vals.categories && Array.isArray(vals.categories) && vals.categories.length ? vals.categories : (vals.category? [vals.category]: []);
      const common = {
        employee_id: vals.employee_id,
        responsible_id: vals.responsible_id,
        frequency_type: vals.frequency_type,
        frequency_value: vals.frequency_type==='every_n_days'? vals.frequency_value: undefined,
        start_date: vals.start_date.format('YYYY-MM-DD'),
        end_date: vals.end_date? vals.end_date.format('YYYY-MM-DD'): undefined,
        remark: vals.remark
      };
      setTaskSaving(true);
      if(editingTask){
        await http.put(`/special-monitorings/tasks/${editingTask.id}`, { ...common, category: editingTask.category }, { headers: authHeader() });
        message.success('任务已更新');
        invalidateAllCaches(); // 清除缓存确保刷新
        await loadTasks(); // 强制重新加载最新任务
      } else {
        await http.post('/special-monitorings/tasks', { ...common, categories }, { headers: authHeader() });
        message.success('任务已创建');
        invalidateAllCaches();
        // 直接重新加载，避免局部插入与统计不一致问题
        await loadTasks();
      }
      await loadCompletion();
      // 新增：任务保存后同步刷新责任人汇总
      if(typeof loadResponsibleSummary === 'function') {
        await loadResponsibleSummary();
      }
      setTaskModalOpen(false);
      setTaskSaving(false);
    } catch(e){
      if(e?.response?.data?.message){
        if(/已有任务/.test(e.response.data.message)){ message.error(e.response.data.message); setTaskSaving(false); return; }
        message.error(e.response.data.message); setTaskSaving(false); return;
      }
      setTaskSaving(false);
      if(e?.errorFields) return;
      message.error(e?.message||'保存任务失败');
    }
  };

  const formatDate = (v)=> v? dayjs(v).format('YYYY-MM-DD'):'—';
  const statusLabelMap = { ongoing:'进行中', paused:'暂停', completed:'已完成', stopped:'已停止' };
  const statusColorMap = { ongoing:'green', paused:'gold', completed:'blue', stopped:'red' };
  const changeTaskStatus = async (task, newStatus)=>{
    if(task.status === newStatus) return;
    if(['completed','stopped'].includes(task.status)){ message.warning('该状态不可再更改'); return; }
    const allowed = { ongoing:['paused','completed','stopped'], paused:['ongoing','completed','stopped'] };
    if(!allowed[task.status]?.includes(newStatus)){ message.warning('不允许的状态切换'); return; }
    try {
  await http.put(`/special-monitorings/tasks/${task.id}`, { status:newStatus }, { headers: authHeader() });
  setTasks(prev=> prev.map(t=> t.id===task.id? { ...t, status:newStatus, next_due_date: newStatus==='ongoing'? t.next_due_date: null }: t));
  invalidateAllCaches();
      loadTasks();
      loadCompletion();
      message.success('状态已更新');
    } catch(e){ message.error(e?.message||'状态更新失败'); }
  };
  const renderStatusCell = (value, record)=>{
    const tag = <Tag color={statusColorMap[value]||'default'}>{statusLabelMap[value]||value}</Tag>;
    if(!canManageTasks) return tag; // 无权限仅展示状态
    if(value==='completed' || value==='stopped') return tag;
    const items = [];
    if(value==='ongoing') items.push({ key:'paused', label:statusLabelMap.paused }, { key:'completed', label:statusLabelMap.completed }, { key:'stopped', label:statusLabelMap.stopped });
    if(value==='paused') items.push({ key:'ongoing', label:statusLabelMap.ongoing }, { key:'completed', label:statusLabelMap.completed }, { key:'stopped', label:statusLabelMap.stopped });
  return <Space size="small">{tag}<Dropdown menu={{ items, onClick:({key})=> changeTaskStatus(record, key) }} trigger={['click']}><span role="button" tabIndex={0} style={{ color:'#1677ff', cursor:'pointer' }} onKeyDown={(e)=>{ if(e.key==='Enter') changeTaskStatus(record, 'ongoing'); }}>切换</span></Dropdown></Space>;
  };
  const taskColumns = [
    { title:'监测对象', dataIndex:'employee_name', width:120, render:(v)=> v||'—' },
    { title:'责任人', dataIndex:'responsible_name', width:110, render:(v)=> v||'—' },
    { title:'类别', dataIndex:'category', width:90 },
    { title:'频率', dataIndex:'frequency_type', width:140, render:(_,r)=>{
      switch(r.frequency_type){
        case 'daily': return '每日1次';
        case 'every_n_days': return `每${r.frequency_value}天1次`;
        case 'weekly': return '每周1次';
        case 'monthly': return '每月1次';
        default: return r.frequency_type;
      }
    } },
    { title:'今日', dataIndex:'expected_today', width:80, render:(_,r)=>{
      if(r.status!=='ongoing') return <Tag>—</Tag>;
      const exp = r.expected_today||0; const act = r.actual_today||0;
      if(exp===0) return <Tag color="default">无</Tag>;
      const color = act>=exp? 'green': (act>0? 'gold':'red');
      return <Tag color={color}>{act}/{exp}</Tag>;
    }},
    { title:'状态', dataIndex:'status', width:110, render:renderStatusCell },
    { title:'开始', dataIndex:'start_date', width:110, render:formatDate },
    { title:'结束', dataIndex:'end_date', width:110, render:formatDate },
    { title:'下次应监测', dataIndex:'next_due_date', width:130, render:(v,r)=> {
      const today = dayjs().format('YYYY-MM-DD');
      const isToday = v===today;
      if(isToday){
        const exp = r.expected_today||0; const act = r.actual_today||0;
        if(exp>0 && act>=exp){
          return <span style={{ fontWeight:600, color:'#16a34a' }}>今日已完成</span>;
        }
      }
      return v? <span style={{ fontWeight:isToday?600:400, color:isToday?'#d4380d':'inherit' }}>{v}{isToday && <Tag color="red" style={{ marginLeft:4 }}>今日</Tag>}</span>:'—';
    } },
    { title:'累计进度', dataIndex:'rate_total', width:160, render:(_,r)=>{
      const exp = r.expected_total||0; const act = r.actual_total||0;
      if(exp===0) return <span style={{ color:'#999' }}>—</span>;
      const percent = Math.min(100, Number(((act/exp)*100).toFixed(2)));
      const color = percent>=90? '#52c41a': (percent>=70? '#faad14':'#ff4d4f');
      return <div style={{ minWidth:140 }}>
        <Tooltip title={`实际 ${act} / 期望 ${exp}`}> <Progress percent={percent} size="small" showInfo={false} strokeColor={color} /> </Tooltip>
        <div style={{ fontSize:11, textAlign:'right', marginTop:2 }}>{act}/{exp}</div>
      </div>;
    }},
    { title:'备注', dataIndex:'remark', ellipsis:true },
    { title:'操作', key:'action', width:200, render:(_,r)=> <Space size="small">
      {canManageTasks && <span role="button" tabIndex={0} onClick={()=>openEditTask(r)} style={{ color:['completed','stopped'].includes(r.status)?'#999':'#1677ff', cursor:['completed','stopped'].includes(r.status)?'not-allowed':'pointer', pointerEvents:['completed','stopped'].includes(r.status)?'none':'auto' }}>编辑</span>}
      {canManageTasks && <span role="button" tabIndex={0} onClick={()=>openCopyTask(r)} style={{ color:'#1677ff', cursor:'pointer' }}>复制</span>}
      {canManageTasks && <span role="button" tabIndex={0} onClick={()=>handleDeleteTask(r)} style={{color:'#ef4444', cursor:'pointer'}}>删除</span>}
      {!canManageTasks && <span style={{ color:'#999', fontSize:12 }}>无任务管理权限</span>}
    </Space> }
  ];

  // 平均完成率改为按总量加权: (Σactual / Σexpected)*100 而不是简单日均 (避免不同日期 expected 数量差异造成偏斜)
  const avgRate = useMemo(()=>{
    // 只统计 expected>0 的日期
    const validDays = completionData.filter(d=> d.expected > 0);
    if(!validDays.length) return null;
    const sumExpected = validDays.reduce((s,d)=> s + (Number(d.expected)||0), 0);
    const sumActual = validDays.reduce((s,d)=> s + (Number(d.actual)||0), 0);
    if(sumExpected === 0) return null;
    const rate = (sumActual / sumExpected) * 100;
    return rate.toFixed(2);
  }, [completionData]);

  // 完成率图表数据映射
  const completionChartData = useMemo(()=> completionData.map(d=> ({ date: d.date, expected: d.expected, actual: d.actual, rate: d.rate })), [completionData]);

  // 类别汇总：进行中任务数、今日应监测任务数、累计完成率（基于任务 expected_total/actual_total）
  const categorySummary = useMemo(()=>{
    const baseCats = CATEGORIES;
    const map = {};
    for(const c of baseCats){
      const catTasks = tasks.filter(t=> t.category===c);
      const ongoing = catTasks.filter(t=> t.status==='ongoing');
      const activeCount = ongoing.length;
      const dueToday = ongoing.filter(t=> t.expected_today>0).length;
      const sumExpected = ongoing.reduce((s,t)=> s + (t.expected_total||0), 0);
      const sumActual = ongoing.reduce((s,t)=> s + (t.actual_total||0), 0);
      const rateTotal = sumExpected===0? null: Number(((sumActual/sumExpected)*100).toFixed(2));
      map[c] = { activeCount, dueToday, rateTotal };
    }
    return map;
  }, [tasks]);

  const handleOpenNew = () => { setEditing(null); setFormVals({ category: activeCat, monitor_date: dayjs().format('YYYY-MM-DD') }); setPhotoFile(null); setPhotoPreview(null); setRemovePhoto(false); setModalOpen(true); };
  const handleEdit = (record)=>{ setEditing(record); setFormVals({ ...record, category: record.category, monitor_date: record.monitor_date }); setPhotoPreview(record.photo_url||null); setPhotoFile(null); setRemovePhoto(false); setModalOpen(true); };

  const handleDelete = async (record)=>{ AntModal.confirm({ title:'确认删除?', content:`删除后不可恢复：${record.category} ${record.monitor_date}`, onOk: async ()=>{ try { await http.delete(`/special-monitorings/${record.id}`, { headers: authHeader() }); message.success('已删除'); fetchList(); fetchStats(); } catch(e){ message.error('删除失败'); } } }); };

  // 基于已有任务员工集合 + 远程搜索 fallback
  const taskEmployeeCache = useMemo(()=>{
    const map = new Map();
    tasks.forEach(t=>{ if(t.employee_id && t.employee_name){ map.set(t.employee_id, t.employee_name); } });
    return Array.from(map.entries()).map(([id,name])=> ({ value:id, label:name }));
  }, [tasks]);
  const searchEmployees = async (kw, setter)=>{
    // 若无关键字，优先展示已有任务关联员工
    if(!kw){ setter(taskEmployeeCache); return; }
    setEmpLoading(true);
    if(debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async ()=>{
      try { const res = await http.get('/employees', { params:{ name: kw }, headers: authHeader() }); const list = Array.isArray(res.data)? res.data: []; const remote = list.slice(0,20).map(e=>({ value:e.id, label: e.name+(e.position? '（'+e.position+'）':'')}));
        // 合并去重(任务缓存 + 远程)
        const mergedMap = new Map(taskEmployeeCache.map(o=> [o.value,o.label]));
        remote.forEach(o=>{ if(!mergedMap.has(o.value)) mergedMap.set(o.value, o.label); });
        setter(Array.from(mergedMap.entries()).map(([value,label])=>({ value,label }))); }
      catch(e){} setEmpLoading(false); }, 300);
  };

  const uploadProps = { maxCount:1, accept:'image/*', beforeUpload:(file)=>{ const isOk = file.size/1024/1024 < 5; if(!isOk){ message.error('图片不能超过5MB'); return Upload.LIST_IGNORE; } setPhotoFile(file); const url = URL.createObjectURL(file); setPhotoPreview(url); setRemovePhoto(false); return false; }, onRemove:()=>{ if(photoPreview?.startsWith('blob:')) URL.revokeObjectURL(photoPreview); setPhotoFile(null); setPhotoPreview(null); } };

  const handleSave = async ()=>{
    // 前置校验
    if(!tasks.length){ message.warning('任务列表尚未加载完成，稍候再试'); return; }
    if(!formVals.monitor_date){ message.warning('请选择监测日期'); return; }
    if(!formVals.responsible_id){ message.warning('请选择责任人'); return; }
    if(!selectedTaskId){ message.warning('请选择监测任务'); return; }
    if(!formVals.monitor_value && !formVals.conclusion){ message.warning('请填写监测值或结论'); return; }
    const chosen = tasks.find(t=> t.id===selectedTaskId);
    if(!chosen){ message.error('所选监测任务不存在或已失效'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('category', chosen.category);
      if(chosen.employee_id) fd.append('employee_id', chosen.employee_id);
      fd.append('responsible_id', formVals.responsible_id);
      fd.append('monitor_date', formVals.monitor_date);
      if(formVals.monitor_value) fd.append('monitor_value', formVals.monitor_value);
      if(formVals.conclusion) fd.append('conclusion', formVals.conclusion);
      if(formVals.remark) fd.append('remark', formVals.remark);
      if(photoFile) fd.append('photo', photoFile);
      if(removePhoto) fd.append('remove_photo','true');
      // 使用 axios 封装的 http，自动带 baseURL 与错误处理；FormData 让浏览器自行设置 multipart 边界
      let json;
      if(editing){
        const resp = await http.put(`/special-monitorings/${editing.id}`, fd, { headers: { ...authHeader(), 'Content-Type': undefined } });
        json = resp.data;
      } else {
        const resp = await http.post('/special-monitorings', fd, { headers: { ...authHeader(), 'Content-Type': undefined } });
        json = resp.data;
      }
      if(!json || json.success===false){ message.error(json?.message||'保存失败'); return; }
      message.success('保存成功');
      setModalOpen(false);
      invalidateAllCaches();
      fetchList(); fetchStats(); loadCompletion();
    } catch(e){ message.error('保存失败'); }
    setSaving(false);
  };

  const frequencyText = useCallback((t)=>{
    switch(t.frequency_type){
      case 'daily': return '每日1次';
      case 'every_n_days': return `每${t.frequency_value}天1次`;
      case 'weekly': return '每周1次';
      case 'monthly': return '每月1次';
      default: return t.frequency_type;
    }
  }, []);

  const handleSaveAndContinue = async ()=>{
    if(!selectedTaskId){ message.warning('请选择监测任务'); return; }
    const beforeTask = selectedTaskId;
    await handleSave();
    // 若已关闭再打开
    setModalOpen(true);
    setSelectedTaskId(beforeTask);
    setFormVals(f=> ({ ...f, monitor_value:'', conclusion:'', remark:'', monitor_date: dayjs().format('YYYY-MM-DD') }));
    setPhotoFile(null); setPhotoPreview(null); setRemovePhoto(false);
  };

  // 今日应监测提示
  const selectedTask = useMemo(()=> tasks.find(t=> t.id===selectedTaskId), [tasks, selectedTaskId]);
  const dueInfo = useMemo(()=>{
    if(!selectedTask || !formVals.monitor_date) return null;
    const target = selectedTask.next_due_date;
    if(!target) return { type:'warn', text:'该任务当前无应监测日期（可能已暂停或结束）' };
    if(formVals.monitor_date === target) return { type:'ok', text:'今日为应监测日' };
    const diff = dayjs(formVals.monitor_date).diff(dayjs(target),'day');
    if(diff>0) return { type:'late', text:`已超过计划日 ${Math.abs(diff)} 天` };
    return { type:'early', text:`非计划日 (提前 ${Math.abs(diff)} 天)` };
  }, [selectedTask, formVals.monitor_date]);

  const [showAllTasksInRecord,setShowAllTasksInRecord] = useState(false);
  const [groupByEmployee,setGroupByEmployee] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false); // 折叠分析区
  const taskSelectOptions = useMemo(()=>{
    const base = tasks.filter(t=> activeCat===ALL_KEY? true : t.category === (formVals.category||activeCat));
    const filtered = showAllTasksInRecord? base : base.filter(t=> t.status==='ongoing');
    const groupMap = new Map();
    for(const t of filtered){
      const groupKey = groupByEmployee? (t.employee_name||'—') : t.category;
      if(!groupMap.has(groupKey)) groupMap.set(groupKey, []);
      groupMap.get(groupKey).push(t);
    }
    const optionGroups = [];
    for(const [grp, list] of groupMap.entries()){
      optionGroups.push({
        label: grp,
        options: list.map(t=> ({
          value: t.id,
          label: (
            <Tooltip title={<div style={{ maxWidth:260 }}>
              <div><strong>{t.employee_name||'—'}</strong> / {t.category}</div>
              <div style={{ fontSize:12, marginTop:4 }}>下次: {t.next_due_date||'—'}</div>
              {t.remark && <div style={{ fontSize:12, color:'#555', marginTop:2 }}>备注: {t.remark}</div>}
            </div>}>
              <span>{t.employee_name||'—'} / {t.category} / {frequencyText(t)}{t.status!=='ongoing'? '（'+(t.status==='paused'?'暂停': (t.status==='completed'?'已完成':'已停止'))+'）':''}</span>
            </Tooltip>
          )
        }))
      });
    }
    return optionGroups;
  }, [tasks, activeCat, formVals.category, showAllTasksInRecord, groupByEmployee, frequencyText]);

  const columns = [
    { title:'日期', dataIndex:'monitor_date', key:'monitor_date', width:110, render:v=> v? dayjs(v).format('YYYY-MM-DD'):'' },
    { title:'类别', dataIndex:'category', key:'category', width:90, render:v=> <Tag color={categoryColors[v]||'blue'}>{v}</Tag> },
    { title:'监测对象', dataIndex:'employee_name', key:'employee_name', width:120, render:(_,r)=> r.employee_name||'—' },
    { title:'责任人', dataIndex:'responsible_name', key:'responsible_name', width:110 },
    { title:'监测值', dataIndex:'monitor_value', key:'monitor_value', width:120, ellipsis:true },
    { title:'结论', dataIndex:'conclusion', key:'conclusion', ellipsis:true },
    { title:'照片', dataIndex:'photo_url', key:'photo_url', width:90, render:(v)=> v? (
      <Tooltip title={<AuthedImage alt='preview' src={v} style={{ maxWidth:200 }} /> }>
        <button
          type="button"
          onClick={async ()=>{ await openWithAuth(v); }}
          style={{ padding: 0, border: 'none', background: 'transparent', color: '#1677ff', cursor: 'pointer' }}
        >查看</button>
      </Tooltip>
    ):'—' },
  { title:'操作', key:'action', width:140, render:(_,record)=> <Space size="small"><span role="button" tabIndex={0} style={{ color:'#1677ff', cursor:'pointer' }} onClick={()=>handleEdit(record)}>编辑</span><span role="button" tabIndex={0} style={{color:'#ef4444', cursor:'pointer'}} onClick={()=>handleDelete(record)}>删除</span></Space> }
  ];

  return (
    <div style={{ padding:24, background:'#f5f7fa', minHeight:'100%' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:600 }}>专项监测工作台</h2>
        <Space>
          {canManageRecords && <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenNew}>新增记录</Button>}
          <Button onClick={()=> setShowAnalytics(s=> !s)}>{showAnalytics? '收起分析':'展开分析'}</Button>
        </Space>
      </div>
  <Tabs activeKey={activeCat} onChange={(k)=>{ setActiveCat(k); setPage(1); }} style={{ background:'#fff', padding:'0 12px', borderRadius:6, marginBottom:16 }} items={[
    { key: ALL_KEY, label:'全部' },
    ...CATEGORIES.map(c=>({ key:c, label:<span style={{ display:'inline-flex', alignItems:'center', gap:6 }}><span style={{ width:8, height:8, borderRadius:'50%', background: (categoryColors[c] && '#'+( {volcano:'fa541c', purple:'722ed1', geekblue:'2f54eb', red:'ff4d4f'}[categoryColors[c]]||'1677ff')) || '#1677ff' }} />{c}</span> }))
  ]} />
      <Card size="small" style={{ marginBottom:16, borderRadius:8 }} bodyStyle={{ padding:'12px 16px' }}>
        <Space wrap>
          <DatePicker.RangePicker onChange={(v)=> setFilters(f=>({...f, dateRange:v}))} />
          <Select allowClear placeholder="监测对象" showSearch filterOption={false} notFoundContent={empLoading? <Spin size="small" />:'无'} style={{ width:150 }} onSearch={(kw)=>searchEmployees(kw,setEmpOptions)} options={empOptions} value={filters.employee} onChange={v=> setFilters(f=>({...f, employee:v}))} />
          <Select allowClear placeholder="责任人" showSearch filterOption={false} notFoundContent={empLoading? <Spin size="small" />:'无'} style={{ width:150 }} onSearch={(kw)=>searchEmployees(kw,setRespOptions)} options={respOptions} value={filters.responsible} onChange={v=> setFilters(f=>({...f, responsible:v}))} />
          <Input placeholder="关键词" allowClear style={{ width:160 }} value={filters.q} onChange={e=> setFilters(f=>({...f, q:e.target.value}))} />
          <Button onClick={()=>{ setPage(1); fetchList(); }}>查询</Button>
          <Button onClick={()=>{ setFilters({ dateRange:null, q:'', responsible:null, employee:null }); setPage(1); fetchList(); }}>重置</Button>
          <Segmented size="small" value={daysRange} onChange={(v)=> setDaysRange(v)} options={[7,14,30]} />
          <Tooltip title={smooth? '当前：平滑曲线 (monotone)':'当前：直线 (linear)'}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
              <Switch size="small" checked={smooth} onChange={setSmooth} />
              <span style={{ fontSize:12 }}>平滑</span>
            </span>
          </Tooltip>
          <Tooltip title="导出当前筛选结果 CSV">
            <Button onClick={()=>{
              const params = new URLSearchParams();
              if(activeCat && activeCat!==ALL_KEY) params.append('category', activeCat);
              if(filters.q) params.append('q', filters.q);
              if(filters.responsible) params.append('responsible_id', filters.responsible);
              if(filters.employee) params.append('employee_id', filters.employee);
              params.append('days', daysRange.toString());
              if(filters.dateRange){ params.append('start_date', filters.dateRange[0].format('YYYY-MM-DD')); params.append('end_date', filters.dateRange[1].format('YYYY-MM-DD')); }
              const url = `/api/special-monitorings/export?${params.toString()}`;
              // 直接触发下载
              const a = document.createElement('a'); a.href = url; a.download = 'special_monitorings.csv'; document.body.appendChild(a); a.click(); document.body.removeChild(a);
            }}>导出</Button>
          </Tooltip>
          <Tooltip title="导出完成率 CSV">
            <Button onClick={()=>{
              if(!completionData.length){ message.info('暂无完成率数据'); return; }
              const header = ['date','expected','actual','rate'];
              const lines = [header.join(',')].concat(completionData.map(d=> [d.date, d.expected, d.actual, d.rate??''].join(',')));
              const blob = new Blob([lines.join('\n')], { type:'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = `completion_${daysRange}d.csv`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
            }}>导出完成率</Button>
          </Tooltip>
        </Space>
      </Card>

      <Row gutter={12} style={{ marginBottom:12 }}>
  {CATEGORIES.map(c=>{ const statFound = stats.counts.find(x=>x.category===c); const summary = categorySummary[c]; const rate = summary?.rateTotal; const rateColor = rate==null? '#d0d5dd': (rate>=90? '#52c41a': (rate>=70? '#faad14':'#ff4d4f')); const isActive = activeCat===c; return <Col key={c} xs={12} sm={6}>
          <Card size="small" hoverable onClick={()=>{ setActiveCat(c); setPage(1); }} style={{ cursor:'pointer', borderRadius:6, padding:0, overflow:'hidden', transition:'all .2s', boxShadow:isActive? '0 0 0 2px rgba(22,119,255,0.3)':'0 1px 2px rgba(0,0,0,0.04)', background:isActive? 'linear-gradient(135deg,#ffffff 0%,#e6f4ff 100%)':'#fafafa', opacity:isActive?1:0.78 }} bodyStyle={{ padding:8 }}>
            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ width:5, height:28, background: isActive? '#1677ff': '#d0d5dd', borderRadius:3 }} />
                <span style={{ fontSize:12, fontWeight:600, color:isActive? '#1677ff':'#555' }}>{c}</span>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:4, fontSize:10 }}>
                <div style={{ display:'flex', flexDirection:'column' }}>
                  <span style={{ color:'#666' }}>进行中</span>
                  <span style={{ fontSize:14, fontWeight:600 }}>{summary.activeCount}</span>
                </div>
                <div style={{ display:'flex', flexDirection:'column' }}>
                  <span style={{ color:'#666' }}>今日应监测</span>
                  <span style={{ fontSize:14, fontWeight:600 }}>{summary.dueToday}</span>
                </div>
                <div style={{ gridColumn:'1 / -1' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <span style={{ color:'#666' }}>完成率</span>
                    <span style={{ fontSize:11, fontWeight:600, color:rateColor }}>{rate==null? '—': rate+'%'}</span>
                  </div>
                  <Progress percent={rate==null? 0: rate} status={rate==null? 'normal': undefined} showInfo={false} size="small" strokeColor={rateColor} style={{ marginTop:2 }} />
                </div>
              </div>
              <div style={{ fontSize:10, color:'#888', marginTop:2 }}>记录：{statFound? statFound.count:0}</div>
            </div>
          </Card>
        </Col>; })}
      </Row>

      {/* 顶部分析区移除，任务管理上移至此位置（以下行保留占位间距） */}
      {canManageTasks && <Card size="small" style={{ borderRadius:8, marginBottom:16 }} title={<span>任务管理 <span style={{ fontSize:12, color:'#666' }}>（关注进度）</span></span>} extra={<Space size={8} wrap>
        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
          <span style={{ fontSize:12, color:'#555' }}>平均完成率（{daysRange}天）:</span>
          {avgRate!==null? <span style={{ fontWeight:600 }}>{avgRate}%</span>: <span style={{ color:'#999' }}>—</span>}
          {avgRate!==null && <Progress percent={Number(avgRate)} showInfo={false} size="small" style={{ width:80 }} strokeColor={Number(avgRate)>=90? '#52c41a': (Number(avgRate)>=70? '#faad14':'#ff4d4f')} />}
        </div>
  <Checkbox checked={showActiveOnly} onChange={e=> setShowActiveOnly(e.target.checked)}>仅进行中</Checkbox>
  {(!canViewAllMonitoring && !isProjectManagerLike) && <Tag color="blue" style={{ userSelect:'none' }}>已按当前用户过滤</Tag>}
        <Button size="small" type="primary" onClick={openNewTask}>新增任务</Button>
      </Space>} bodyStyle={{ padding:12 }}>
        <div style={{ height:220, marginBottom:12 }}>
          {completionChartData.length? <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={completionChartData} margin={{ top:10, right:20, left:0, bottom:10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize:10 }} />
              <YAxis yAxisId="left" label={{ value:'次数', angle:-90, position:'insideLeft', offset:10 }} />
              <YAxis yAxisId="right" orientation="right" label={{ value:'完成率%', angle:-90, position:'insideRight', offset:10 }} />
              <ReTooltip content={({ active, payload })=>{
                if(active && payload && payload.length){
                  const p = payload[0].payload;
                  return <div style={{ background:'#fff', border:'1px solid #ddd', padding:8, fontSize:12 }}>
                    <div style={{ fontWeight:600 }}>{p.date}</div>
                    <div>期望: {p.expected}</div>
                    <div>实际: {p.actual}</div>
                    <div>完成率: {p.rate==null? '—': p.rate+'%'}</div>
                  </div>;
                }
                return null;
              }} />
              <Legend wrapperStyle={{ fontSize:11 }} />
              <Bar yAxisId="left" dataKey="expected" name="期望" stackId="cnt" fill="#94a3b8" />
              <Bar yAxisId="left" dataKey="actual" name="实际" stackId="cnt" fill="#16a34a" />
              <Line yAxisId="right" type={smooth? 'monotone':'linear'} dataKey="rate" name="完成率" stroke="#2563eb" strokeWidth={2} dot={{ r:2 }} activeDot={{ r:4 }} />
            </ComposedChart>
          </ResponsiveContainer>: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ fontSize:12 }}>暂无完成率数据</span>} />}
        </div>
        <Table rowKey="id" size="small" columns={taskColumns} dataSource={tasks} pagination={false} locale={{ emptyText:'暂无任务' }} />
      </Card>}
      {!canManageTasks && <Card size="small" style={{ borderRadius:8, marginBottom:16 }} bodyStyle={{ padding:12 }}><div style={{ fontSize:12, color:'#888' }}>您没有任务管理权限（需要 special_monitoring_task_manage）。</div></Card>}

      <Row gutter={16} style={{ marginBottom:16 }}>
        <Col xs={24} xl={13} style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <Card size="small" style={{ borderRadius:8 }} title={<span>记录列表 <span style={{ fontSize:12, color:'#666' }}>（HR 录入）</span></span>} bodyStyle={{ padding:12 }} extra={<Space size={4} wrap>
            {(!canViewAllMonitoring && !isProjectManagerLike) && <Tag color="blue" style={{ userSelect:'none' }}>已按当前用户过滤</Tag>}
            {canManageRecords && <Button size="small" type="primary" onClick={handleOpenNew}>新增记录</Button>}
          </Space>}>
            <Table
              rowKey="id"
              size="small"
              columns={columns}
              dataSource={data}
              loading={loading}
              onRow={(record)=> ({ onClick:()=> setSelectedRecordForTrend(record) })}
              rowClassName={(r)=> r.id === (selectedRecordForTrend && selectedRecordForTrend.id)? 'row-selected-monitoring': ''}
              pagination={{ size:'small', showSizeChanger:true, current:page, pageSize, total, onChange:(p,ps)=>{ setPage(p); setPageSize(ps); } }}
            />
            {!canManageRecords && <div style={{ marginTop:8, fontSize:12, color:'#888' }}>当前账号没有新增记录权限（需要 special_monitoring_record_manage）。</div>}
          </Card>
        </Col>
        <Col xs={24} xl={11} style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {isHr ? (
            <Card size="small" style={{ borderRadius:8 }} title={<span style={{ fontSize:12, fontWeight:600 }}>监测对象趋势{selectedRecordForTrend? `：${selectedRecordForTrend.employee_name||''} / ${selectedRecordForTrend.category}`:''}</span>} bodyStyle={{ padding:12 }} extra={<Space size={4} wrap>
              <Segmented size="small" value={daysRange} onChange={(v)=> setDaysRange(v)} options={[7,14,30]} />
              {selectedRecordForTrend && <Button size="small" onClick={()=> loadRecordTrend(selectedRecordForTrend.employee_id, selectedRecordForTrend.category)}>刷新</Button>}
            </Space>}>
              {!selectedRecordForTrend && <div style={{ height:240, display:'flex', alignItems:'center', justifyContent:'center' }}><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ fontSize:12 }}>点击记录行查看趋势</span>} /></div>}
              {selectedRecordForTrend && <div style={{ height:260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={recordTrend} margin={{ top:10, right:24, left:0, bottom:10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize:10 }} />
                    <YAxis yAxisId="left" label={{ value:'监测值', angle:-90, position:'insideLeft', offset:10 }} />
                    <ReTooltip content={({ active, payload })=>{
                      if(active && payload && payload.length){
                        const p = payload[0].payload;
                        return <div style={{ background:'#fff', border:'1px solid #ddd', padding:8, fontSize:12 }}>
                          <div style={{ fontWeight:600 }}>{p.date}</div>
                          {selectedRecordForTrend?.category==='高血压' && (p.systolic||p.diastolic) && <div>血压: {p.systolic??'?'}/{p.diastolic??'?'}{p.value!==undefined && (p.systolic===undefined||p.diastolic===undefined) && ` (${p.value})`}</div>}
                          {selectedRecordForTrend?.category!=='高血压' && p.value!==undefined && <div>数值: {p.value}</div>}
                          {p.rawValues && p.rawValues.length>0 && <div style={{ marginTop:4 }}>原始: {p.rawValues.join('，')}</div>}
                        </div>;
                      }
                      return null;
                    }} />
                    <Legend wrapperStyle={{ fontSize:11 }} />
                    {selectedRecordForTrend?.category==='高血压' ? (
                      <>
                        <Line yAxisId="left" type={smooth? 'monotone':'linear'} dataKey="systolic" name="收缩压" stroke="#ef4444" strokeWidth={2} dot={{ r:3 }} activeDot={{ r:5 }} />
                        <Line yAxisId="left" type={smooth? 'monotone':'linear'} dataKey="diastolic" name="舒张压" stroke="#3b82f6" strokeWidth={2} dot={{ r:3 }} activeDot={{ r:5 }} />
                      </>
                    ) : (
                      <Line yAxisId="left" type={smooth? 'monotone':'linear'} dataKey="value" name="监测值" stroke="#16a34a" strokeWidth={2} dot={{ r:3 }} activeDot={{ r:5 }} />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
                {recordTrendLoading && <div style={{ position:'absolute', top:12, right:12 }}><Spin size="small" /></div>}
              </div>}
            </Card>
          ) : (
            <Card size="small" style={{ borderRadius:8 }} title={<span style={{ fontSize:12, fontWeight:600 }}>责任人任务完成情况（进行中）</span>} bodyStyle={{ padding:12 }} extra={<Space size={4} wrap>
              {responsibleSummary && responsibleSummary.length>0 && <Tooltip title="导出责任人汇总 CSV"><Button size="small" onClick={()=>{
                const header = ['responsible_id','responsible_name','expected','actual','pending','rate','end_latest'];
                const lines = [header.join(',')].concat(responsibleSummary.map(r=> [r.responsible_id??'', r.responsible_name||'', r.expected, r.actual, r.pending, r.rate==null? '': r.rate, r.end_latest||''].join(',')));
                const blob = new Blob([lines.join('\n')], { type:'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href=url; a.download='responsible_summary.csv'; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
              }}>导出</Button></Tooltip>}
              <Button size="small" onClick={()=> invalidateAllCaches() || loadResponsibleSummary()}>刷新</Button>
            </Space>}>
              {(!responsibleSummary || !responsibleSummary.length) && <div style={{ height:240, display:'flex', alignItems:'center', justifyContent:'center' }}><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ fontSize:12 }}>暂无进行中任务或责任人数据</span>} /></div>}
              {responsibleSummary && responsibleSummary.length>0 && <div style={{ height:260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={responsibleSummary.map(r=> ({ name: (r.responsible_name||'责任人')+(r.responsible_id? `(${r.responsible_id})`:''), expected:r.expected, actual:r.actual, rate:r.rate, pending:r.pending, end_latest:r.end_latest }))} margin={{ top:10, right:20, left:0, bottom:10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" interval={0} tick={{ fontSize:10 }} />
                    <YAxis yAxisId="left" label={{ value:'次数', angle:-90, position:'insideLeft', offset:10 }} />
                    <YAxis yAxisId="right" orientation="right" label={{ value:'完成率%', angle:-90, position:'insideRight', offset:10 }} />
                    <ReTooltip content={({ active, payload })=>{
                      if(active && payload && payload.length){
                        const p = payload[0].payload;
                        return <div style={{ background:'#fff', border:'1px solid #ddd', padding:8, fontSize:12 }}>
                          <div style={{ fontWeight:600 }}>{p.name}</div>
                          <div>期望: {p.expected}</div>
                          <div>实际: {p.actual}</div>
                          <div>待完成: {p.pending}</div>
                          <div>完成率: {p.rate==null? '—': p.rate+'%'}</div>
                          {p.end_latest && <div>最近结束: {p.end_latest}</div>}
                        </div>;
                      }
                      return null;
                    }} />
                    <Legend wrapperStyle={{ fontSize:11 }} />
                    <Bar yAxisId="left" dataKey="expected" name="期望" stackId="cnt" fill="#94a3b8" />
                    <Bar yAxisId="left" dataKey="actual" name="实际" stackId="cnt" fill="#16a34a" />
                    <Line yAxisId="right" type={smooth? 'monotone':'linear'} dataKey="rate" name="完成率" stroke="#2563eb" strokeWidth={2} dot={{ r:3 }} activeDot={{ r:5 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>}
            </Card>
          )}
        </Col>
      </Row>

      <WideModal isOpen={taskModalOpen} onClose={()=> setTaskModalOpen(false)} title={(editingTask? '编辑':'新增')+'监测任务'+(copySource? '（复制）':'')} size="normal">
        <Form form={taskForm} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="employee_id" label="监测对象 *" rules={[{ required:true, message:'请选择监测对象'}]}>
                <Select showSearch placeholder="输入姓名搜索" filterOption={false} onSearch={(kw)=>searchEmployees(kw,setEmpOptions)} options={empOptions} disabled={!!editingTask} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="responsible_id" label="责任人 *" rules={[{ required:true, message:'请选择责任人'}]}>
                <Select showSearch placeholder="输入姓名搜索" filterOption={false} onSearch={(kw)=>searchEmployees(kw,setRespOptions)} options={respOptions} />
              </Form.Item>
            </Col>

            <Col span={24}>
              {!editingTask && <Form.Item name="categories" label="监测类别 *" extra={activeCat===ALL_KEY? '当前为“全部”视图，请选择一个或多个具体类别':'可多选'} rules={[{ validator:(_,v)=>{ if(!v||!v.length) return Promise.reject('请选择至少一个类别'); return Promise.resolve(); } }]}>
                <Checkbox.Group options={CATEGORIES.map(c=>({ label:c, value:c }))} />
              </Form.Item>}
              {editingTask && <Form.Item name="category" label="类别 *" rules={[{ required:true }]}>
                <Select disabled options={CATEGORIES.map(c=>({ value:c, label:c }))} />
              </Form.Item>}
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="频率类型 *" name="frequency_type" rules={[{ required:true }]}>
                <Radio.Group optionType="button">
                  <Radio.Button value="daily">每日</Radio.Button>
                  <Radio.Button value="every_n_days">每N天</Radio.Button>
                  <Radio.Button value="weekly">每周</Radio.Button>
                  <Radio.Button value="monthly">每月</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              {taskFrequencyType==='every_n_days' && <Form.Item name="frequency_value" label="N 天间隔 *" rules={[{ required:true, message:'请输入N(2-365)', pattern:/^([2-9]|[1-9]\d|1\d\d|2\d\d|3[0-5]\d)$/ }]}>
                <Input placeholder="例如 3 表示每3天一次" />
              </Form.Item>}
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="start_date" label="开始日期 *" rules={[{ required:true }]}>
                <DatePicker style={{ width:'100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="end_date" label="结束日期">
                <DatePicker style={{ width:'100%' }} />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="remark" label="备注">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <div style={{ textAlign:'center', marginTop:12 }}>
          <Space>
            <Button
              type="primary"
              loading={taskSaving}
              disabled={!taskEmployeeId || !taskResponsibleId || (!editingTask && (!taskCategories || !taskCategories.length))}
              onClick={handleSaveTask}
            >
              保存
            </Button>
            <Button onClick={()=> setTaskModalOpen(false)} disabled={taskSaving}>取消</Button>
          </Space>
        </div>
      </WideModal>

      <WideModal isOpen={modalOpen} onClose={()=> { setModalOpen(false); setSelectedTaskId(null); }} title={(editing?'编辑':'新增')+'专项监测'} size="wide">
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:16 }}>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8, flexWrap:'wrap' }}>
              <label style={{ margin:0 }}>监测任务 *</label>
              <div style={{ display:'flex', alignItems:'center', gap:12, fontSize:12, color:'#666' }}>
                <Checkbox size="small" checked={showAllTasksInRecord} onChange={e=> setShowAllTasksInRecord(e.target.checked)}>显示全部</Checkbox>
                <Checkbox size="small" checked={groupByEmployee} onChange={e=> setGroupByEmployee(e.target.checked)}>按员工分组</Checkbox>
              </div>
            </div>
            <Select
              placeholder={showAllTasksInRecord? '选择任务（含非进行中）':'选择进行中任务'}
              options={taskSelectOptions}
              value={selectedTaskId}
              onChange={(v)=>{ setSelectedTaskId(v); const t = tasks.find(x=> x.id===v); if(t){ setFormVals(f=>({ ...f, category:t.category, employee_id:t.employee_id, responsible_id: t.responsible_id || f.responsible_id })); } }}
              showSearch
              optionFilterProp="label"
              filterOption={(input, option)=> (option?.label||'').toLowerCase().includes(input.toLowerCase()) }
            />
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            <label>监测日期 *</label>
            <Input type="date" value={formVals.monitor_date} onChange={e=> setFormVals(f=>({...f, monitor_date:e.target.value}))} />
            {dueInfo && <div style={{ fontSize:12, lineHeight:1.4, color: dueInfo.type==='ok'? '#16a34a': (dueInfo.type==='warn'? '#d97706': (dueInfo.type==='late'? '#dc2626':'#2563eb')) }}>{dueInfo.text}</div>}
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            <label>自动派生：类别 / 对象</label>
            <Input disabled value={selectedTaskId? `${formVals.category||''} / ${ (tasks.find(t=> t.id===selectedTaskId)?.employee_name)||'—'}`:''} placeholder="选择监测任务后自动显示" />
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            <label>责任人 *</label>
            <Select placeholder="输入姓名" showSearch filterOption={false} notFoundContent={empLoading? <Spin size="small" />:'无'} onSearch={(kw)=>searchEmployees(kw,setRespOptions)} options={respOptions} value={formVals.responsible_id} onChange={v=> setFormVals(f=>({...f, responsible_id:v}))} />
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            <label>监测值</label>
            <Input value={formVals.monitor_value||''} onChange={e=> setFormVals(f=>({...f, monitor_value:e.target.value}))} placeholder="如：120/80" />
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            <label>结论</label>
            <Input value={formVals.conclusion||''} onChange={e=> setFormVals(f=>({...f, conclusion:e.target.value}))} placeholder="如：正常 / 复测 / 异常" />
          </div>
          <div style={{ gridColumn:'1 / -1', display:'flex', flexDirection:'column', gap:6 }}>
            <label>备注</label>
            <Input.TextArea rows={3} value={formVals.remark||''} onChange={e=> setFormVals(f=>({...f, remark:e.target.value}))} />
          </div>
          <div style={{ gridColumn:'1 / -1', display:'flex', flexDirection:'column', gap:6 }}>
            <label>监测照片 (单张 ≤5MB)</label>
            <Upload {...uploadProps} fileList={photoFile? [{ uid:'-1', name: photoFile.name, status:'done' }]: (photoPreview? [{ uid:'-2', name: formVals.photo_name||'已上传', status:'done', url: photoPreview }]: [])} listType="text">
              <Button icon={<UploadOutlined />}>选择图片</Button>
            </Upload>
            {photoPreview && <div style={{ marginTop:8 }}><AuthedImage alt="preview" src={photoPreview} style={{ maxWidth:220, border:'1px solid #e2e8f0', padding:4, borderRadius:4 }} /></div>}
            {photoPreview && !photoFile && <Button size="small" danger onClick={()=>{ setRemovePhoto(true); setPhotoPreview(null); }}>移除已存在照片</Button>}
          </div>
        </div>
        <div style={{ marginTop:24, textAlign:'center' }}>
          <Space>
            <Button type="primary" loading={saving} disabled={!selectedTaskId || !formVals.responsible_id || !formVals.monitor_date} onClick={handleSave}>保存</Button>
            <Button loading={saving} disabled={!selectedTaskId || !formVals.responsible_id || !formVals.monitor_date} onClick={handleSaveAndContinue}>保存并继续</Button>
            <Button onClick={()=> setModalOpen(false)} disabled={saving}>取消</Button>
          </Space>
        </div>
      </WideModal>
      {/* 左下角当前登录信息显示 */}
      {user && <div style={{ position:'fixed', left:12, bottom:8, fontSize:11, color:'#555', background:'rgba(255,255,255,0.9)', padding:'4px 8px', border:'1px solid #e2e8f0', borderRadius:6, boxShadow:'0 1px 2px rgba(0,0,0,0.05)' }}>
        <span style={{ fontWeight:600 }}>当前角色</span>: <span style={{ marginLeft:4 }}><strong>{primaryRoleDesc}</strong> / 姓名: <strong>{user.name || '—'}</strong></span>
      </div>}
    </div>
  );
};

export default SpecialMonitoringPage;